import { useCallback, useEffect, useRef, useState } from 'react'
import { CloudArrowUpIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { HoverHelp } from '@/tableControls'
import type { CaseListItem, ModelSummaryResponse } from '@/services/projections'
import { useProjectionsStore } from '@/store/projectionsStore'
import { downloadJsonFile } from '@/utils/downloadFile'
import { todayIsoDate } from '@/utils/waterfallCalculator'
import { buildTemplate, parseCaseFile, type FileIssue, type ParsedCaseFile } from './caseJson'
import type { SchemaEntry } from './engine/types'
import { useDialogFocusTrap } from './useDialogFocusTrap'

export const UPLOAD_MAX_BYTES = 5 * 1024 * 1024

export function uploadFileValidationError(file: File): string | null {
  if (file.size > UPLOAD_MAX_BYTES) {
    return 'File is too large. Maximum size is 5 MB.'
  }
  const isJson =
    file.type === 'application/json' || file.name.toLowerCase().endsWith('.json')
  if (!isJson) {
    return 'Only JSON files can be uploaded.'
  }
  return null
}

type UploadCaseDialogProps = {
  open: boolean
  schema: SchemaEntry[]
  model: ModelSummaryResponse | null
  cases: CaseListItem[]
  onClose: () => void
  onContinueCreate: (parsed: ParsedCaseFile, fileName: string) => void
  onContinueOverride: (parsed: ParsedCaseFile, fileName: string) => void
}

function basisMonthLabel(lastClosedMonth: string): string {
  return lastClosedMonth.length >= 7 ? lastClosedMonth.slice(0, 7) : lastClosedMonth
}

function renderSummary(parsed: ParsedCaseFile) {
  const summary = parsed.summary
  if (!summary) return null
  const viewLines = Object.entries(summary.drivenLinesByView)
    .map(([view, count]) => `${count} in ${view}`)
    .join(', ')
  const driverCounts = Object.entries(summary.driverCounts)
    .map(([driver, count]) => `${count} ${driver}`)
    .join(', ')
  return (
    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600">
      {viewLines ? <li>Driven lines: {viewLines}</li> : null}
      {driverCounts ? <li>Drivers: {driverCounts}</li> : null}
      {summary.recipeLineCount > 0 ? (
        <li>
          {summary.recipeLineCount} lines with recipes · {summary.overrideCount} overrides
        </li>
      ) : null}
      <li>One-time items: {summary.oneTimeItemCount}</li>
      <li>Cash policy: {summary.cashPolicyEnabled ? 'on' : 'off'}</li>
      <li>Notes: {summary.noteCount}</li>
      {summary.rebaseDroppedOneTimeItems != null ? (
        <li>Rebase dropped one-time items: {summary.rebaseDroppedOneTimeItems}</li>
      ) : null}
      {summary.rebaseDroppedNotes != null ? (
        <li>Rebase dropped notes: {summary.rebaseDroppedNotes}</li>
      ) : null}
      {summary.undrivenTemplateLineCount > 0 ? (
        <li>Undriven template lines: {summary.undrivenTemplateLineCount}</li>
      ) : null}
    </ul>
  )
}

const GROUPED_WARNING_CODES = new Set(['file_rationale_missing'])

function groupFileIssues(issues: FileIssue[]): Array<FileIssue | { code: string; count: number; message: string }> {
  const grouped = new Map<string, FileIssue[]>()
  const ungrouped: FileIssue[] = []
  for (const issue of issues) {
    if (issue.severity === 'warning' && GROUPED_WARNING_CODES.has(issue.code)) {
      const list = grouped.get(issue.code) ?? []
      list.push(issue)
      grouped.set(issue.code, list)
    } else {
      ungrouped.push(issue)
    }
  }
  const groupedRows = Array.from(grouped.entries()).map(([code, items]) => ({
    code,
    count: items.length,
    message: items[0]?.message ?? code,
  }))
  return [...ungrouped, ...groupedRows]
}

function renderIssues(issues: FileIssue[]) {
  const errors = issues.filter((issue) => issue.severity === 'error')
  const driftWarnings = issues.filter(
    (issue) => issue.severity === 'warning' && issue.code === 'recipe_anchor_drift',
  )
  const otherWarnings = groupFileIssues(
    issues.filter(
      (issue) => issue.severity === 'warning' && issue.code !== 'recipe_anchor_drift',
    ),
  )
  return (
    <div className="mt-3 space-y-3">
      {errors.length > 0 ? (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-red-700">Errors</p>
          <ul className="mt-1 space-y-1 text-sm text-red-700">
            {errors.map((issue, index) => (
              <li key={`e-${index}`}>
                {issue.path ? <span className="font-mono text-xs">{issue.path}: </span> : null}
                {issue.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {driftWarnings.length > 0 ? (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-amber-700">
            Anchors that differ from history
          </p>
          <ul className="mt-1 space-y-1 text-sm text-amber-800">
            {driftWarnings.map((issue, index) => (
              <li key={`d-${index}`}>{issue.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {otherWarnings.length > 0 ? (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Warnings</p>
          <ul className="mt-1 space-y-1 text-sm text-amber-800">
            {otherWarnings.map((issue, index) => {
              if ('count' in issue && issue.count > 1) {
                return (
                  <li key={`w-${index}`}>
                    {issue.count}× {issue.code}: {issue.message}
                  </li>
                )
              }
              const row = issue as FileIssue
              return (
                <li key={`w-${index}`}>
                  {row.path ? <span className="font-mono text-xs">{row.path}: </span> : null}
                  {row.message}
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

export default function UploadCaseDialog({
  open,
  schema,
  model,
  cases,
  onClose,
  onContinueCreate,
  onContinueOverride,
}: UploadCaseDialogProps) {
  const [dragging, setDragging] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)
  const [fileText, setFileText] = useState<string | null>(null)
  const [readError, setReadError] = useState<string | null>(null)
  const [parsed, setParsed] = useState<ParsedCaseFile | null>(null)
  const [rebaseChecked, setRebaseChecked] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dragCounterRef = useRef(0)
  const dialogRef = useDialogFocusTrap(open, onClose)

  const history = useProjectionsStore((state) => state.history)
  const lastClosedMonth = model?.latest_import?.last_closed_month ?? ''
  const schemaFingerprint = model?.schema_fingerprint ?? ''
  const fileMonth = parsed?.file?.basis.last_closed_month
  const currentMonth = basisMonthLabel(lastClosedMonth)
  const rebaseNeeded = parsed?.rebase?.needed === true
  const awaitingHistory = fileText != null && history == null

  const parseText = useCallback(
    (text: string, rebase: boolean) => {
      if (!history) {
        return null
      }
      const recipes = (model?.settings?.recipes ?? {}) as {
        anchor_drift_pct?: number
        rationale_max_words?: number
      }
      const existingCases = cases.map((caseItem) => ({
        name: caseItem.name,
        expires_on: caseItem.expires_on,
        expired: caseItem.expired,
      }))
      return parseCaseFile(text, {
        schema,
        lastClosedMonth,
        schemaFingerprint,
        existingCases,
        today: todayIsoDate(),
        rebase,
        history,
        recipeDefaults: {
          anchor_drift_pct: recipes.anchor_drift_pct,
          rationale_max_words: recipes.rationale_max_words,
        },
      })
    },
    [cases, history, lastClosedMonth, model?.settings?.recipes, schema, schemaFingerprint],
  )

  useEffect(() => {
    if (fileText && history) {
      setParsed(parseText(fileText, rebaseChecked))
    }
  }, [fileText, history, rebaseChecked, parseText])

  const resetFile = () => {
    setFileName(null)
    setFileText(null)
    setReadError(null)
    setParsed(null)
    setRebaseChecked(false)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const acceptFile = (file: File) => {
    setReadError(null)
    setParsed(null)
    setRebaseChecked(false)
    const validationError = uploadFileValidationError(file)
    if (validationError) {
      setReadError(validationError)
      setFileName(file.name)
      setFileText(null)
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const text = String(reader.result ?? '')
      setFileName(file.name)
      setFileText(text)
      setParsed(history ? parseText(text, false) : null)
    }
    reader.onerror = () => {
      setReadError('Could not read the selected file.')
      setFileName(file.name)
      setFileText(null)
    }
    reader.readAsText(file)
  }

  const handleRebaseChange = (checked: boolean) => {
    setRebaseChecked(checked)
    if (fileText && history) {
      setParsed(parseText(fileText, checked))
    }
  }

  const handleBlankTemplate = () => {
    if (!lastClosedMonth || !schemaFingerprint) return
    const file = buildTemplate({
      schema,
      lastClosedMonth,
      schemaFingerprint,
    })
    const month = basisMonthLabel(lastClosedMonth)
    downloadJsonFile(`projection-case-template_${month}.json`, file)
  }

  const hasErrors = (parsed?.issues.some((issue) => issue.severity === 'error') ?? false) ||
    parsed?.target.kind === 'blocked' ||
    parsed?.inputs == null
  const primaryDisabled =
    awaitingHistory || hasErrors || (rebaseNeeded && !rebaseChecked)
  const isOverride = parsed?.target.kind === 'override'
  const caseName =
    parsed?.target.kind === 'override'
      ? parsed.target.name
      : parsed?.file?.case.name?.trim() ?? ''

  const handlePrimary = () => {
    if (!parsed || !fileName || primaryDisabled) return
    if (isOverride) {
      onContinueOverride(parsed, fileName)
    } else {
      onContinueCreate(parsed, fileName)
    }
  }

  if (!open) {
    return null
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-case-title"
        className="relative flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-5 py-4">
          <h3 id="upload-case-title" className="text-sm font-semibold text-gray-900">
            Upload JSON
          </h3>
        </div>
        <div className="overflow-y-auto px-5 py-4">
          {!parsed && !awaitingHistory ? (
            <>
              <div
                className={`rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
                  dragging
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
                onDragEnter={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  dragCounterRef.current += 1
                  setDragging(true)
                }}
                onDragLeave={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  dragCounterRef.current -= 1
                  if (dragCounterRef.current <= 0) {
                    dragCounterRef.current = 0
                    setDragging(false)
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  dragCounterRef.current = 0
                  setDragging(false)
                  const file = e.dataTransfer.files[0]
                  if (file) acceptFile(file)
                }}
              >
                <CloudArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-700">
                  Drag and drop a JSON file here, or choose a file below.
                </p>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => inputRef.current?.click()}
                >
                  Choose file
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) acceptFile(file)
                  }}
                />
              </div>
              {readError ? <p className="mt-3 text-sm text-red-600">{readError}</p> : null}
              <button
                type="button"
                className="mt-4 text-sm text-brand-700 hover:text-brand-800"
                onClick={handleBlankTemplate}
              >
                Download blank template
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-700">
                File: <span className="font-medium">{fileName}</span>
              </p>
              {awaitingHistory ? (
                <p className="mt-2 text-sm text-gray-600">Loading history…</p>
              ) : null}
              {parsed && parsed.target.kind === 'create' ? (
                <p className="mt-2 text-sm text-gray-700">
                  Creates new case <strong>{caseName}</strong>
                </p>
              ) : parsed && parsed.target.kind === 'override' ? (
                <p className="mt-2 text-sm text-gray-700">
                  Overrides existing case <strong>{caseName}</strong>
                </p>
              ) : null}
              {parsed ? renderSummary(parsed) : null}
              {parsed ? renderIssues(parsed.issues) : null}
              {parsed && rebaseNeeded && fileMonth ? (
                <label className="mt-4 flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={rebaseChecked}
                    onChange={(event) => handleRebaseChange(event.target.checked)}
                  />
                  <span>
                    Align by calendar month (file was written for {fileMonth}; model now starts{' '}
                    {currentMonth})
                  </span>
                </label>
              ) : null}
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <Button variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          {parsed || awaitingHistory ? (
            <>
              <Button variant="outline" size="md" onClick={resetFile}>
                Choose a different file
              </Button>
              <HoverHelp
                aria-label="Continue help"
                label={
                  <Button
                    variant="primary"
                    size="md"
                    disabled={primaryDisabled}
                    onClick={handlePrimary}
                  >
                    {awaitingHistory
                      ? 'Loading history…'
                      : isOverride
                        ? 'Continue'
                        : 'Create case'}
                  </Button>
                }
              >
                {awaitingHistory
                  ? 'History must finish loading before continuing'
                  : rebaseNeeded && !rebaseChecked
                    ? 'Align by calendar month before continuing'
                    : hasErrors
                      ? 'Fix errors in the file before continuing'
                      : isOverride
                        ? 'Continue to override confirmation'
                        : 'Create case from file'}
              </HoverHelp>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
